import type { ComponentChildren } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { Controller, useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form'
import { Check, ChevronDown, ChevronUp, GripVertical, Lock, Plus, Save, Trash2, X } from 'lucide-preact'
import type { QuizDto, WebViewDto } from '@/api/webs'
import { TextBox, TextBoxField, NumberInput, Checkbox } from '@/components'
import { CopyButton } from '@/components/common/CopyButton'
import { SegmentedToggle } from '@/components/common/SegmentedToggle'
import { getFieldLabelClasses } from '@/components/common/formLabelUtils'
import { useDragReorder } from '@/hooks/useDragReorder'
import { fieldLimits } from '@/types/field-limits.generated'
import { quizIdFromText } from '@/utils/quizId'
import { bandStripGeometry } from '@/utils/quizBands'
import type { QuizDraftTouched } from '@/utils/quizDraft'
import { hasSameQuizStructure } from '@/utils/quizStructure'
import { QuizHeading } from './QuizHeading'
import { QuizBandStrip } from './QuizBandStrip'

export interface QuizFormValues { quiz: QuizDto }

interface Props {
    web: WebViewDto
    form: UseFormReturn<QuizFormValues>
    saving: boolean
    onSave: () => void
    onCancel: () => void
    onRemove: () => void
    /** Rows Webster's drafts added or changed since the editor opened, tagged until Save or Cancel (ORB-926). */
    suggested: QuizDraftTouched
    /** Reloads the editor from the signed-off version. */
    onGoBackToSignedOff: () => void
}

type ItemListPath = 'quiz.Questions' | 'quiz.Dimensions' | 'quiz.Bands' | 'quiz.ReportSections'

const LIMITS = fieldLimits.Quiz
const LABEL = getFieldLabelClasses('compact')
const SECONDARY_BUTTON = 'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
const ADD_BUTTON = 'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
const DELETE_ICON_BUTTON = 'p-1.5 h-fit rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 dark:text-slate-500 dark:hover:bg-red-900/20 dark:hover:text-red-400'
const COLUMN_HEADER = 'mt-4 grid gap-x-5 pb-2 border-b border-slate-200 text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400'
const DISABLED_INPUT = 'disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-400'
const NUMBER_INPUT = 'px-2.5! py-1.5! text-sm rounded-md! tabular-nums'
const NUMBER_INPUT_OVERLAP = 'border-amber-400! ring-1 ring-amber-300 dark:border-amber-500! dark:ring-amber-700'

const LOCK_NOTE = 'People have answered this quiz, so its questions, dimensions, bands and scoring are locked. You can still change the wording. To change the quiz itself, create a new web.'

function SectionHeader({ title, count, note }: { title: string; count?: number; note: string }) {
    return (
        <div className="flex items-baseline justify-between gap-6">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {title}{count != null && <span className="ml-1 text-sm font-normal tabular-nums text-slate-500 dark:text-slate-400">{count}</span>}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">{note}</p>
        </div>
    )
}

function SuggestedTag() {
    return <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium leading-4 bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-900/20 dark:text-primary-400">Suggested by Webster</span>
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
    return <div className="mt-4"><button type="button" onClick={onClick} className={ADD_BUTTON}><Plus className="w-4 h-4" />{label}</button></div>
}

/** An ID input with its copy button, optionally prefixed "ID" as on the dimension and band rows. */
function IdBox({ name, value, label, prefixed, disabled, error, onChange, onBlur }: { name: string; value: string; label: string; prefixed?: boolean; disabled: boolean; error?: string; onChange: (next: string) => void; onBlur?: () => void }) {
    return (
        <div>
            <div className="relative">
                {prefixed && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 dark:text-slate-400">ID</span>}
                <TextBox
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    density="compact"
                    maxLength={LIMITS.QuizId.MaxLength}
                    disabled={disabled}
                    className={`font-mono text-[13px]! pr-9! ${prefixed ? 'pl-9!' : ''} ${DISABLED_INPUT}`}
                />
                <span className="absolute right-1 top-1/2 -translate-y-1/2"><CopyButton text={value} label={label} /></span>
            </div>
            {error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
    )
}

function ResultRow({ title, hint, tagged, children }: { title: string; hint: string; tagged?: boolean; children: ComponentChildren }) {
    return (
        <div className="grid grid-cols-[15rem_minmax(0,1fr)] gap-x-8 py-5">
            <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{title}</p><p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
                {tagged && <div className="mt-2.5"><SuggestedTag /></div>}
            </div>
            {children}
        </div>
    )
}

/**
 * The quiz editor. Mounted only while editing, so its field arrays register against a quiz the panel
 * has just reset. Band overlaps are drawn here from the unsaved values but never block Save: whether
 * the bands cover 0 to 100 is a sign-off readiness item, and the server words it.
 */
export function QuizEditor({ web, form, saving, onSave, onCancel, onRemove, onGoBackToSignedOff, suggested }: Props) {
    const { control, register, setValue, getValues, formState: { errors } } = form
    const quiz = useWatch({ control, name: 'quiz' })
    const isLocked = (web.Download?.QuizSubmissionCount ?? 0) > 0
    const signedOffQuiz = web.Download?.SignedOffQuiz ?? null
    const signedOffVersion = web.Download?.SignedOffQuizVersion

    const questions = useFieldArray({ control, name: 'quiz.Questions' })
    const dimensions = useFieldArray({ control, name: 'quiz.Dimensions' })
    const bands = useFieldArray({ control, name: 'quiz.Bands' })
    const sections = useFieldArray({ control, name: 'quiz.ReportSections' })
    const { isDragOver, dragProps } = useDragReorder(questions.move)

    // The signed-off IDs count as saved even when the working copy has dropped them, so going back to that version
    // keeps them.
    const savedIds = useMemo(() => {
        const saved = [web.Download?.Quiz, web.Download?.SignedOffQuiz]
        const idsOf = (items: (quiz: QuizDto) => Array<{ Id: string }>) => new Set(saved.flatMap(quiz => (quiz ? items(quiz).map(item => item.Id) : [])))
        return {
            'quiz.Questions': idsOf(quiz => quiz.Questions),
            'quiz.Dimensions': idsOf(quiz => quiz.Dimensions),
            'quiz.Bands': idsOf(quiz => quiz.Bands),
            'quiz.ReportSections': idsOf(quiz => quiz.ReportSections),
        }
    }, [web.Download?.Quiz, web.Download?.SignedOffQuiz])
    // An item's ID follows its text until it is pinned: saved, typed by the author, or put in by a Webster draft. Pinned by
    // value, not by row key, because every Put in editor re-keys the field arrays.
    const [pinnedIds] = useState(() => ({
        'quiz.Questions': new Set(savedIds['quiz.Questions']),
        'quiz.Dimensions': new Set(savedIds['quiz.Dimensions']),
        'quiz.Bands': new Set(savedIds['quiz.Bands']),
        'quiz.ReportSections': new Set(savedIds['quiz.ReportSections']),
    }))
    const draftedIds: Record<ItemListPath, string[]> = {
        'quiz.Questions': suggested.questionIds,
        'quiz.Dimensions': suggested.dimensionIds,
        'quiz.Bands': suggested.bandIds,
        'quiz.ReportSections': suggested.reportSectionIds,
    }
    const isPinned = (path: ItemListPath, id: string) => pinnedIds[path].has(id) || draftedIds[path].includes(id)

    const otherIds = (path: ItemListPath, exceptIndex: number) =>
        (getValues(path) as Array<{ Id: string }>).filter((_, index) => index !== exceptIndex).map(item => item.Id)
    const suggestId = (path: ItemListPath, text: string, fallback: string, exceptIndex = -1) => {
        const taken = otherIds(path, exceptIndex)
        return quizIdFromText(text, taken) || quizIdFromText(fallback, taken)
    }

    /** Questions point at dimensions by ID, so a dimension's new ID carries its questions with it. */
    const questionsIn = (dimensionId: string) =>
        getValues('quiz.Questions').flatMap((question, questionIndex) => (question.DimensionId === dimensionId ? [questionIndex] : []))
    const setDimensionId = (index: number, nextId: string, questionIndexes = questionsIn(getValues(`quiz.Dimensions.${index}.Id`))) => {
        setValue(`quiz.Dimensions.${index}.Id`, nextId, { shouldDirty: true })
        questionIndexes.forEach(questionIndex => setValue(`quiz.Questions.${questionIndex}.DimensionId`, nextId, { shouldDirty: true }))
    }
    // A typed ID passes through other values on the way, and one of them can be another dimension's ID or blank, the
    // DimensionId of a question with no dimension yet. Matching by value there would take those questions too, so
    // typing carries the questions the dimension had when it began, and an ID left clashing or blank goes back.
    const dimensionIdTyping = useRef<{ index: number; startId: string; startPins: Set<string>; questionIndexes: number[] } | null>(null)
    const startDimensionIdTyping = (index: number) => {
        if (dimensionIdTyping.current?.index === index) return dimensionIdTyping.current
        const startId = getValues(`quiz.Dimensions.${index}.Id`)
        dimensionIdTyping.current = { index, startId, startPins: new Set(pinnedIds['quiz.Dimensions']), questionIndexes: questionsIn(startId) }
        return dimensionIdTyping.current
    }
    const isDimensionIdTaken = (index: number, id: string) => otherIds('quiz.Dimensions', index).includes(id)
    const finishDimensionId = (index: number) => {
        const typing = dimensionIdTyping.current
        dimensionIdTyping.current = null
        if (typing?.index !== index) return
        const id = getValues(`quiz.Dimensions.${index}.Id`)
        if (id !== '' && !isDimensionIdTaken(index, id)) return
        // Going back undoes the typing's pins too, or the other dimension's ID it clashed with would stay pinned.
        pinnedIds['quiz.Dimensions'].clear()
        typing.startPins.forEach(id => pinnedIds['quiz.Dimensions'].add(id))
        setDimensionId(index, typing.startId, typing.questionIndexes)
    }
    const setItemId = (path: ItemListPath, index: number, nextId: string) => {
        const previousId = getValues(`${path}.${index}.Id`)
        if (path === 'quiz.Dimensions') setDimensionId(index, nextId)
        else setValue(`${path}.${index}.Id`, nextId, { shouldDirty: true })
        if (path === 'quiz.Questions') setExpandedId(current => (current === previousId ? nextId : current))
    }
    const typeId = (path: ItemListPath, index: number, nextId: string) => {
        const dimensionTyping = path === 'quiz.Dimensions' ? startDimensionIdTyping(index) : null
        const previousId = getValues(`${path}.${index}.Id`)
        if (!otherIds(path, index).includes(previousId)) pinnedIds[path].delete(previousId)
        // Blank is where a new row starts, so pinning it would stop every blank row following its text.
        if (nextId !== '') pinnedIds[path].add(nextId)
        if (dimensionTyping) setDimensionId(index, nextId, dimensionTyping.questionIndexes)
        else setItemId(path, index, nextId)
    }
    const followText = (path: ItemListPath, index: number, text: string, fallback: string) => {
        if (isPinned(path, getValues(`${path}.${index}.Id`))) return
        setItemId(path, index, suggestId(path, text, fallback, index))
    }

    // The open question is tracked by its ID, like the pins, so a Put in editor doesn't close it.
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const expandNewQuestion = useRef(false)
    useEffect(() => {
        if (!expandNewQuestion.current) return
        expandNewQuestion.current = false
        setExpandedId(questions.fields[questions.fields.length - 1]?.Id ?? null)
    }, [questions.fields.length])

    const addQuestion = () => {
        expandNewQuestion.current = true
        questions.append({ Id: suggestId('quiz.Questions', '', 'question'), Text: '', LowLabel: '', HighLabel: '', HelpText: null, DimensionId: '', AllowsNotApplicable: false, NotApplicableLabel: null, IsReverseScored: false })
    }
    const newDimension = () => {
        const id = suggestId('quiz.Dimensions', '', 'dimension')
        dimensions.append({ Id: id, Name: '', LowAdvice: null, MidAdvice: null, HighAdvice: null })
        return id
    }
    const addBand = () => bands.append({ Id: suggestId('quiz.Bands', '', 'band'), Name: '', MinScore: 0, MaxScore: 0, WhatThisMeans: null, WhatToDo: null })
    const addSection = () => sections.append({ Id: suggestId('quiz.ReportSections', '', 'section'), Title: '', Text: '' })

    const scale = quiz.ScaleMax
    const dimensionName = new Map(quiz.Dimensions.map(dimension => [dimension.Id, dimension.Name || dimension.Id]))
    const questionCount = (dimensionId: string) => quiz.Questions.filter(question => question.DimensionId === dimensionId).length
    const bandGeometry = bandStripGeometry(quiz.Bands)
    const isOverlapping = (bandIndex: number, field: 'MinScore' | 'MaxScore') =>
        bandGeometry.overlappingFields.some(item => item.bandIndex === bandIndex && item.field === field)
    const wholeNumber = (value: number) => Number.isInteger(value) || 'Whole number'

    return (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <QuizHeading web={web}>
                <div className="shrink-0 flex items-center gap-2">
                    <button type="button" onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
                    <button type="button" onClick={onCancel} disabled={saving} className={SECONDARY_BUTTON}><X className="w-4 h-4" /> Cancel</button>
                </div>
            </QuizHeading>

            {isLocked && (
                <div className="mx-6 mb-5 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                    <Lock className="w-4 h-4 mt-0.5 shrink-0 text-slate-400 dark:text-slate-500" />
                    <div>
                        <p>{LOCK_NOTE}</p>
                        {signedOffQuiz && signedOffVersion != null && !hasSameQuizStructure(quiz, signedOffQuiz) && (
                            <p className="mt-1.5">
                                The structure here differs from Version {signedOffVersion}, the one people answered, so the web can't be signed off with it.{' '}
                                <button type="button" onClick={onGoBackToSignedOff} className="font-medium text-primary-700 hover:underline dark:text-primary-400">Go back to Version {signedOffVersion}</button>
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-slate-50 border-y border-slate-200 px-6 py-5 flex items-start gap-14 dark:bg-slate-900 dark:border-slate-700">
                <div>
                    <span className={LABEL}>Scale</span>
                    <SegmentedToggle
                        ariaLabel="Scale"
                        value={String(scale)}
                        onChange={value => setValue('quiz.ScaleMax', Number(value), { shouldDirty: true })}
                        options={[{ value: '5', label: '1-5' }, { value: '10', label: '1-10' }]}
                    />
                </div>
                <div>
                    <span className={LABEL}>Ask for details</span>
                    <SegmentedToggle
                        ariaLabel="Ask for details"
                        value={quiz.DetailsPlacement}
                        onChange={value => setValue('quiz.DetailsPlacement', value, { shouldDirty: true })}
                        options={[{ value: 'Before', label: 'Before the questions' }, { value: 'After', label: 'After the questions' }]}
                    />
                    <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                        {quiz.DetailsPlacement === 'Before'
                            ? 'First name, last name and email, asked before the first question.'
                            : 'First name, last name and email, asked once the last question is answered.'}
                    </p>
                </div>
            </div>

            <div className="px-6 pt-8 pb-6 space-y-12">
                <div>
                    <SectionHeader title="Questions" count={questions.fields.length} note="Drag to reorder" />
                    {questions.fields.length > 0 && (
                        <ol className="mt-4 border-y border-slate-200 divide-y divide-slate-200 dark:border-slate-700 dark:divide-slate-700">
                            {questions.fields.map((field, index) => {
                                const question = quiz.Questions[index] ?? field
                                const expanded = expandedId === question.Id
                                const isNew = !savedIds['quiz.Questions'].has(question.Id)
                                const isSuggested = suggested.questionIds.includes(question.Id)
                                const grip = (
                                    <button type="button" title="Drag to reorder" className={`p-1 rounded text-slate-400 cursor-grab dark:text-slate-500 ${expanded ? 'hover:bg-slate-200 dark:hover:bg-slate-600' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}`}><GripVertical className="w-4 h-4" /></button>
                                )
                                const number = <span className="text-sm tabular-nums text-slate-500 dark:text-slate-400">{index + 1}</span>
                                const dragOver = isDragOver(index) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                if (!expanded) return (
                                    <li key={field.id}>
                                        <div {...dragProps(index)} className={`grid grid-cols-[1.75rem_1.5rem_minmax(0,1fr)_13rem_9rem_2rem] items-center gap-x-3 px-2 py-3 ${dragOver}`}>
                                            {grip}
                                            {number}
                                            <div className="min-w-0">
                                                {question.Text
                                                    ? <p className="truncate text-sm text-slate-900 dark:text-white">{question.Text}</p>
                                                    : <p className="truncate text-sm text-slate-400 dark:text-slate-500">No wording yet</p>}
                                                <p className={`${isSuggested ? 'mt-1' : 'mt-0.5'} flex items-center gap-2 min-w-0`}><span className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{question.Id}</span>{isSuggested && <SuggestedTag />}</p>
                                            </div>
                                            <span className="truncate text-sm text-slate-600 dark:text-slate-300">{dimensionName.get(question.DimensionId) ?? ''}</span>
                                            <span />
                                            <button type="button" title="Edit question" onClick={() => setExpandedId(question.Id)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"><ChevronDown className="w-4 h-4" /></button>
                                        </div>
                                    </li>
                                )
                                return (
                                    <li key={field.id} className={`bg-slate-50 px-2 pt-3 pb-6 dark:bg-slate-900 ${dragOver}`}>
                                        <div {...dragProps(index)} className="grid grid-cols-[1.75rem_1.5rem_minmax(0,1fr)_13rem_9rem_2rem] items-center gap-x-3">
                                            {grip}
                                            {number}
                                            <div className="min-w-0 flex items-center gap-2">
                                                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{isNew ? 'New question' : question.Text || 'No wording yet'}</p>
                                                {isSuggested && <SuggestedTag />}
                                            </div>
                                            <span />
                                            <span />
                                            <button type="button" title="Collapse" onClick={() => setExpandedId(null)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-600"><ChevronUp className="w-4 h-4" /></button>
                                        </div>
                                        <div className="mt-4 ml-[3.75rem] mr-2 grid grid-cols-2 gap-x-6 gap-y-5">
                                            <Controller control={control} name={`quiz.Questions.${index}.Text`} render={({ field: text }) => (
                                                <TextBox
                                                    name={text.name}
                                                    label="Question"
                                                    density="compact"
                                                    multiline
                                                    rows={2}
                                                    maxLength={LIMITS.QuizQuestionText.MaxLength}
                                                    containerClassName="col-span-2"
                                                    value={text.value ?? ''}
                                                    onBlur={text.onBlur}
                                                    onChange={next => { text.onChange(next); followText('quiz.Questions', index, next, 'question') }}
                                                />
                                            )} />
                                            <div>
                                                <label htmlFor={`quiz.Questions.${index}.Id`} className={LABEL}>ID</label>
                                                <IdBox name={`quiz.Questions.${index}.Id`} value={question.Id} label="question ID" disabled={isLocked} onChange={next => typeId('quiz.Questions', index, next)} />
                                                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">Filled in from the question. You can change it until the first answer arrives.</p>
                                            </div>
                                            <div />
                                            <div className="col-span-2">
                                                <span className={LABEL}>Dimension</span>
                                                <div role="radiogroup" aria-label="Dimension" className="flex flex-wrap items-center gap-2">
                                                    {quiz.Dimensions.map(dimension => {
                                                        const selected = dimension.Id === question.DimensionId
                                                        return (
                                                            <button
                                                                key={dimension.Id}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={selected}
                                                                onClick={() => setValue(`quiz.Questions.${index}.DimensionId`, dimension.Id, { shouldDirty: true })}
                                                                className={selected
                                                                    ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-md border border-primary-600 bg-primary-50 text-primary-800 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                                                                    : 'px-2.5 py-1.5 text-sm rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}
                                                            >
                                                                {selected && <Check className="w-3.5 h-3.5" />}{dimension.Name || dimension.Id}
                                                            </button>
                                                        )
                                                    })}
                                                    <button type="button" onClick={() => setValue(`quiz.Questions.${index}.DimensionId`, newDimension(), { shouldDirty: true })} className="inline-flex items-center gap-1 px-1.5 py-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"><Plus className="w-3.5 h-3.5" />New dimension</button>
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor={`quiz.Questions.${index}.LowLabel`} className={LABEL}>Low label <span className="font-normal text-slate-500 dark:text-slate-400">shown at 1</span></label>
                                                <TextBoxField control={control} name={`quiz.Questions.${index}.LowLabel`} density="compact" maxLength={LIMITS.QuizScaleLabel.MaxLength} />
                                            </div>
                                            <div>
                                                <label htmlFor={`quiz.Questions.${index}.HighLabel`} className={LABEL}>High label <span className="font-normal text-slate-500 dark:text-slate-400">shown at {scale}</span></label>
                                                <TextBoxField control={control} name={`quiz.Questions.${index}.HighLabel`} density="compact" maxLength={LIMITS.QuizScaleLabel.MaxLength} />
                                            </div>
                                            <TextBoxField control={control} name={`quiz.Questions.${index}.HelpText`} label="Help text" density="compact" maxLength={LIMITS.QuizHelpText.MaxLength} helperText="Shown under the question." containerClassName="col-span-2" />
                                            <div className="col-span-2 grid grid-cols-2 gap-x-6 pt-1">
                                                <div>
                                                    <Checkbox label="Not applicable allowed" description="Adds an option that takes the question out of the score instead of counting it as zero." {...register(`quiz.Questions.${index}.AllowsNotApplicable`)} />
                                                    {question.AllowsNotApplicable && (
                                                        <div className="mt-3 ml-[1.625rem]">
                                                            <TextBoxField control={control} name={`quiz.Questions.${index}.NotApplicableLabel`} label="Not applicable option" density="compact" maxLength={LIMITS.QuizNotApplicableLabel.MaxLength} placeholder="Not applicable" />
                                                        </div>
                                                    )}
                                                </div>
                                                <Checkbox label="Reverse scored" description={`A ${scale} scores as 1. Use it when a high answer is the weaker position.`} {...register(`quiz.Questions.${index}.IsReverseScored`)} />
                                            </div>
                                        </div>
                                        <div className="mt-6 ml-[3.75rem]">
                                            <button type="button" onClick={() => { questions.remove(index); setExpandedId(null) }} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"><Trash2 className="w-4 h-4" />Delete question</button>
                                        </div>
                                    </li>
                                )
                            })}
                        </ol>
                    )}
                    <AddButton label="Add question" onClick={addQuestion} />
                </div>

                <div>
                    <SectionHeader title="Dimensions" count={dimensions.fields.length} note="Each question belongs to one" />
                    {dimensions.fields.length > 0 && (
                        <>
                            <div className={`${COLUMN_HEADER} grid-cols-[15rem_repeat(3,minmax(0,1fr))_2rem]`}><div>Name and ID</div><div>Low advice</div><div>Mid advice</div><div>High advice</div><div /></div>
                            <div className="divide-y divide-slate-200 border-b border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                                {dimensions.fields.map((field, index) => {
                                    const dimension = quiz.Dimensions[index] ?? field
                                    const count = questionCount(dimension.Id)
                                    return (
                                        <div key={field.id} className="grid grid-cols-[15rem_repeat(3,minmax(0,1fr))_2rem] gap-x-5 py-5">
                                            <div className="space-y-2.5">
                                                <Controller control={control} name={`quiz.Dimensions.${index}.Name`} render={({ field: name }) => (
                                                    <TextBox
                                                        name={name.name}
                                                        density="compact"
                                                        placeholder="Dimension name"
                                                        maxLength={LIMITS.QuizDimensionName.MaxLength}
                                                        value={name.value ?? ''}
                                                        onBlur={name.onBlur}
                                                        onChange={next => { name.onChange(next); followText('quiz.Dimensions', index, next, 'dimension') }}
                                                    />
                                                )} />
                                                <IdBox
                                                    name={`quiz.Dimensions.${index}.Id`}
                                                    value={dimension.Id}
                                                    label="dimension ID"
                                                    prefixed
                                                    disabled={isLocked}
                                                    error={isDimensionIdTaken(index, dimension.Id) ? 'Another dimension has this ID' : undefined}
                                                    onChange={next => typeId('quiz.Dimensions', index, next)}
                                                    onBlur={() => finishDimensionId(index)}
                                                />
                                                <p className="pt-0.5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">{count} {count === 1 ? 'question' : 'questions'}{suggested.dimensionIds.includes(dimension.Id) && <SuggestedTag />}</p>
                                            </div>
                                            <TextBoxField control={control} name={`quiz.Dimensions.${index}.LowAdvice`} density="compact" multiline rows={6} maxLength={LIMITS.QuizAdvice.MaxLength} placeholder="What to tell someone who scores low here" className="leading-relaxed" />
                                            <TextBoxField control={control} name={`quiz.Dimensions.${index}.MidAdvice`} density="compact" multiline rows={6} maxLength={LIMITS.QuizAdvice.MaxLength} placeholder="What to tell someone in the middle" className="leading-relaxed" />
                                            <TextBoxField control={control} name={`quiz.Dimensions.${index}.HighAdvice`} density="compact" multiline rows={6} maxLength={LIMITS.QuizAdvice.MaxLength} placeholder="What to tell someone who scores high here" className="leading-relaxed" />
                                            <button type="button" title="Delete dimension" onClick={() => dimensions.remove(index)} className={DELETE_ICON_BUTTON}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                    <AddButton label="Add dimension" onClick={newDimension} />
                </div>

                <div>
                    <SectionHeader title="Bands" count={bands.fields.length} note="Scored out of 100. Together they cover 0 to 100." />
                    {bands.fields.length > 0 && (
                        <>
                            <div className="mt-4"><QuizBandStrip bands={quiz.Bands} geometry={bandGeometry} /></div>
                            <div className={`${COLUMN_HEADER} mt-6! grid-cols-[15rem_4.5rem_4.5rem_repeat(2,minmax(0,1fr))_2rem]`}><div>Name and ID</div><div>From</div><div>To</div><div>What this means</div><div>What to do</div><div /></div>
                            <div className="divide-y divide-slate-200 border-b border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                                {bands.fields.map((field, index) => {
                                    const band = quiz.Bands[index] ?? field
                                    return (
                                        <div key={field.id} className="grid grid-cols-[15rem_4.5rem_4.5rem_repeat(2,minmax(0,1fr))_2rem] gap-x-5 py-5 items-start">
                                            <div className="space-y-2.5">
                                                <Controller control={control} name={`quiz.Bands.${index}.Name`} render={({ field: name }) => (
                                                    <TextBox
                                                        name={name.name}
                                                        density="compact"
                                                        placeholder="Band name"
                                                        maxLength={LIMITS.QuizBandName.MaxLength}
                                                        value={name.value ?? ''}
                                                        onBlur={name.onBlur}
                                                        onChange={next => { name.onChange(next); followText('quiz.Bands', index, next, 'band') }}
                                                    />
                                                )} />
                                                <IdBox name={`quiz.Bands.${index}.Id`} value={band.Id} label="band ID" prefixed disabled={isLocked} onChange={next => typeId('quiz.Bands', index, next)} />
                                                {suggested.bandIds.includes(band.Id) && <div className="pt-0.5"><SuggestedTag /></div>}
                                            </div>
                                            <NumberInput
                                                aria-label="From"
                                                min={0}
                                                max={100}
                                                step={1}
                                                error={errors.quiz?.Bands?.[index]?.MinScore?.message}
                                                className={`${NUMBER_INPUT} ${isOverlapping(index, 'MinScore') ? NUMBER_INPUT_OVERLAP : ''}`}
                                                {...register(`quiz.Bands.${index}.MinScore`, { valueAsNumber: true, validate: wholeNumber })}
                                            />
                                            <NumberInput
                                                aria-label="To"
                                                min={0}
                                                max={100}
                                                step={1}
                                                error={errors.quiz?.Bands?.[index]?.MaxScore?.message}
                                                className={`${NUMBER_INPUT} ${isOverlapping(index, 'MaxScore') ? NUMBER_INPUT_OVERLAP : ''}`}
                                                {...register(`quiz.Bands.${index}.MaxScore`, { valueAsNumber: true, validate: wholeNumber })}
                                            />
                                            <TextBoxField control={control} name={`quiz.Bands.${index}.WhatThisMeans`} density="compact" multiline rows={4} maxLength={LIMITS.QuizBandCopy.MaxLength} placeholder="What this score means" className="leading-relaxed" />
                                            <TextBoxField control={control} name={`quiz.Bands.${index}.WhatToDo`} density="compact" multiline rows={4} maxLength={LIMITS.QuizBandCopy.MaxLength} placeholder="What to do next" className="leading-relaxed" />
                                            <button type="button" title="Delete band" onClick={() => bands.remove(index)} className={DELETE_ICON_BUTTON}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                    <AddButton label="Add band" onClick={addBand} />
                </div>

                <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">Results</h3>
                    <div className="mt-4 border-t border-slate-200 divide-y divide-slate-200 dark:border-slate-700 dark:divide-slate-700">
                        <ResultRow title="Button" hint="On the results page, in the email and in the report">
                            <div className="grid grid-cols-2 gap-x-6 pr-12">
                                <TextBoxField control={control} name="quiz.ButtonLabel" label="Label" density="compact" maxLength={LIMITS.QuizButtonLabel.MaxLength} placeholder="Book a call" helperText="Leave it empty to use “Book a call”." />
                                <TextBoxField control={control} name="quiz.ButtonUrl" label="Link" type="url" density="compact" maxLength={LIMITS.QuizButtonUrl.MaxLength} placeholder="https://" />
                            </div>
                        </ResultRow>
                        <ResultRow title="Report sections" hint="In the PDF report, after the plan">
                            <div className="space-y-6">
                                {sections.fields.map((field, index) => (
                                    <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_2rem] gap-x-4">
                                        <div className="space-y-3">
                                            <Controller control={control} name={`quiz.ReportSections.${index}.Title`} render={({ field: title }) => (
                                                <div>
                                                    <label htmlFor={title.name} className={LABEL}>
                                                        Title
                                                        {suggested.reportSectionIds.includes(quiz.ReportSections[index]?.Id ?? field.Id) && <span className="ml-2 align-middle"><SuggestedTag /></span>}
                                                    </label>
                                                    <TextBox
                                                        name={title.name}
                                                        density="compact"
                                                        maxLength={LIMITS.QuizReportSectionTitle.MaxLength}
                                                        value={title.value ?? ''}
                                                        onBlur={title.onBlur}
                                                        onChange={next => { title.onChange(next); followText('quiz.ReportSections', index, next, 'section') }}
                                                    />
                                                </div>
                                            )} />
                                            <TextBoxField control={control} name={`quiz.ReportSections.${index}.Text`} label="Text" density="compact" multiline rows={3} maxLength={LIMITS.QuizReportSectionText.MaxLength} className="leading-relaxed" />
                                        </div>
                                        <button type="button" title="Delete section" onClick={() => sections.remove(index)} className={`mt-6 ${DELETE_ICON_BUTTON}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))}
                                <div><button type="button" onClick={addSection} className={ADD_BUTTON}><Plus className="w-4 h-4" />Add section</button></div>
                            </div>
                        </ResultRow>
                        <ResultRow title="Disclaimer" hint="At the end of the report" tagged={suggested.disclaimer}>
                            <div className="pr-12">
                                <TextBoxField control={control} name="quiz.Disclaimer" density="compact" multiline rows={2} maxLength={LIMITS.QuizDisclaimer.MaxLength} className="leading-relaxed" />
                            </div>
                        </ResultRow>
                    </div>
                </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                <button type="button" onClick={onRemove} disabled={saving} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 disabled:opacity-50 dark:text-red-400 dark:hover:text-red-300"><Trash2 className="w-4 h-4" />Remove quiz</button>
            </div>
        </section>
    )
}
